import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { rolesApi } from '../../api/roles.api';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const roleColors = {
  superadmin: 'danger',
  admin: 'primary',
  basic: 'secondary'
};

const columns = [
  {
    key: 'name',
    label: 'Name',
    render: (row) => (
      <Badge variant={roleColors[row.name] || 'secondary'}>
        {row.name}
      </Badge>
    )
  },
  { key: 'description', label: 'Description' },
  {
    key: 'permissions',
    label: 'Permissions',
    render: (row) => row.permissions?.join(', ') || '-'
  }
];

export default function RolesListPage() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await rolesApi.getAll();
      setRoles(response.data.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await rolesApi.delete(deleteId);
      setDeleteId(null);
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Roles</h1>
        <Button onClick={() => navigate('/roles/new')}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Role
        </Button>
      </div>

      <DataTable
        columns={[
          ...columns,
          {
            key: 'actions',
            label: 'Actions',
            align: 'right',
            render: (row) => (
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => navigate(`/roles/${row._id}/edit`)}
                  className="p-1 text-gray-500 hover:text-primary-600"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setDeleteId(row._id)}
                  className="p-1 text-gray-500 hover:text-red-600"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )
          }
        ]}
        data={roles}
        loading={loading}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Role"
        message="Are you sure you want to delete this role?"
        confirmText="Delete"
        loading={deleting}
      />
    </DashboardLayout>
  );
}
